import { FC, KeyboardEvent, useEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { format, parseISO } from "date-fns";
import {
  useAdminOrganizations,
  useRemovals,
  useTenantPreview,
  useCreateRemoval,
  useRestoreRemoval,
  useRetryRemoval,
  AdminOrganization,
  TenantRemovalRow,
  TenantRemovalStatus,
} from "lib/adminQueries";
import { useDialogA11y } from "components/dialog/useDialogA11y";
import "./AdminTenants.scss";

/** Statuses in which the tenant is locked out and must not be offered again. */
const ACTIVE: TenantRemovalStatus[] = ["SCHEDULED", "PURGING"];

type Tab = "tenants" | "removals";

const when = (iso: string | null): string =>
  iso ? format(parseISO(iso), "dd-MM-yyyy HH:mm") : "—";

/**
 * Super_admin tenant removal: schedule a complete removal, watch it run, undo
 * it while it is still scheduled.
 *
 * Two tabs — a table of tenants that can still be scheduled, and the table of
 * removals already in flight or finished. Deleting a tenant opens a
 * confirmation popup scoped to that one row; the operator must type the
 * tenant's exact name before the destructive action enables. The failure that
 * actually happens here is removing the wrong tenant, and only the specific
 * name catches that — anything generic becomes muscle memory within a week.
 */
const AdminTenants: FC = () => {
  const { t } = useTranslation();
  const [tab, setTab] = useState<Tab>("tenants");
  const [deletingTenant, setDeletingTenant] = useState<AdminOrganization | null>(null);
  const [confirmName, setConfirmName] = useState("");

  const tenantsTabRef = useRef<HTMLButtonElement>(null);
  const removalsTabRef = useRef<HTMLButtonElement>(null);
  const dialogRef = useRef<HTMLDivElement>(null);

  const orgsQuery = useAdminOrganizations();
  const removalsQuery = useRemovals();
  const previewQuery = useTenantPreview(deletingTenant?.id ?? null);

  const createRemoval = useCreateRemoval();
  const restoreRemoval = useRestoreRemoval();
  const retryRemoval = useRetryRemoval();

  const removals = useMemo<TenantRemovalRow[]>(
    () => removalsQuery.data ?? [],
    [removalsQuery.data]
  );

  // A tenant already being removed must not be selectable a second time —
  // otherwise an operator could schedule a second removal on top of one that
  // is already scheduled or purging.
  const lockedOut = useMemo(
    () =>
      new Set(
        removals.filter((r) => ACTIVE.includes(r.status)).map((r) => r.organizationId)
      ),
    [removals]
  );

  const organizations = useMemo(
    () => (orgsQuery.data ?? []).filter((o) => !lockedOut.has(o.id)),
    [orgsQuery.data, lockedOut]
  );

  // Automatic-activation ARIA pattern: selecting a tab (by click or by
  // keyboard) just changes `tab`; the effect below is the single place that
  // then moves focus to it. Keeping focus-on-tab-change in one spot — rather
  // than calling .focus() inline here too — is what lets a *programmatic*
  // tab switch (the post-removal jump to Removals, below) pick up the same
  // behavior for free instead of racing the dialog's own focus-restore.
  const selectTab = (next: Tab) => {
    setTab(next);
  };

  const onTabsKeyDown = (e: KeyboardEvent<HTMLButtonElement>) => {
    switch (e.key) {
      case "ArrowRight":
      case "ArrowLeft":
        e.preventDefault();
        selectTab(tab === "tenants" ? "removals" : "tenants");
        break;
      case "Home":
        e.preventDefault();
        selectTab("tenants");
        break;
      case "End":
        e.preventDefault();
        selectTab("removals");
        break;
      default:
        break;
    }
  };

  // Opening the popup for a different tenant must never carry over a name
  // typed for a previous one, so both open and close reset it.
  const openDeleteDialog = (org: AdminOrganization): void => {
    setDeletingTenant(org);
    setConfirmName("");
  };

  const closeDeleteDialog = (): void => {
    setDeletingTenant(null);
    setConfirmName("");
  };

  useDialogA11y({
    isOpen: deletingTenant !== null,
    onClose: closeDeleteDialog,
    containerRef: dialogRef,
  });

  // Declared after useDialogA11y so that within one commit its cleanup (which
  // restores focus to the Delete button that opened the dialog) runs first;
  // this effect then wins the last word. That matters for the one case where
  // both fire together — a successful removal closes the dialog AND flips
  // `tab` to "removals" in the same act — where restoring focus to a Delete
  // button that's now sitting in a hidden panel would drop focus to <body>.
  // Skips the initial mount so landing on the page doesn't steal focus.
  const isFirstTabRender = useRef(true);
  useEffect(() => {
    if (isFirstTabRender.current) {
      isFirstTabRender.current = false;
      return;
    }
    (tab === "tenants" ? tenantsTabRef : removalsTabRef).current?.focus();
  }, [tab]);

  const nameMatches =
    deletingTenant !== null && confirmName.trim() === deletingTenant.name;

  const submit = (): void => {
    if (!deletingTenant || !nameMatches) return;
    createRemoval.mutate(
      { organizationId: deletingTenant.id, confirmName: confirmName.trim() },
      {
        onSuccess: () => {
          closeDeleteDialog();
          // Switch to Removals so the operator sees the result of what they
          // just scheduled, rather than a table that no longer lists it.
          setTab("removals");
        },
      }
    );
  };

  return (
    <div className="admin-tenants">
      <div className="admin-tenants__header">
        <h1>{t("adminTenants.title")}</h1>
        <p className="admin-tenants__subtitle">{t("adminTenants.intro")}</p>
      </div>

      <div className="admin-tenants__tabs" role="tablist">
        <button
          ref={tenantsTabRef}
          type="button"
          role="tab"
          id="admin-tenants-tab-tenants"
          aria-selected={tab === "tenants"}
          aria-controls="admin-tenants-panel-tenants"
          tabIndex={tab === "tenants" ? 0 : -1}
          className={tab === "tenants" ? "is-active" : ""}
          onClick={() => setTab("tenants")}
          onKeyDown={onTabsKeyDown}
        >
          {t("adminTenants.tabTenants")}
        </button>
        <button
          ref={removalsTabRef}
          type="button"
          role="tab"
          id="admin-tenants-tab-removals"
          aria-selected={tab === "removals"}
          aria-controls="admin-tenants-panel-removals"
          tabIndex={tab === "removals" ? 0 : -1}
          className={tab === "removals" ? "is-active" : ""}
          onClick={() => setTab("removals")}
          onKeyDown={onTabsKeyDown}
        >
          {t("adminTenants.tabRemovals")}
        </button>
      </div>

      <div
        className="admin-tenants__panel"
        role="tabpanel"
        id="admin-tenants-panel-tenants"
        aria-labelledby="admin-tenants-tab-tenants"
        data-testid="admin-tenants-panel-tenants"
        hidden={tab !== "tenants"}
        tabIndex={0}
      >
        <div className="admin-tenants__card admin-tenants__list">
          <div className="admin-tenants__table-wrap">
            <table className="admin-tenants__table">
              <thead>
                <tr>
                  <th>{t("adminTenants.colTenant")}</th>
                  <th aria-label={t("adminTenants.colActions")} />
                </tr>
              </thead>
              <tbody>
                {organizations.length === 0 && (
                  <tr>
                    <td colSpan={2} className="admin-tenants__empty">
                      {t("adminTenants.tenantsEmpty")}
                    </td>
                  </tr>
                )}
                {organizations.map((o) => (
                  <tr key={o.id}>
                    <td>{o.name}</td>
                    <td className="admin-tenants__actions">
                      <button
                        type="button"
                        className="admin-tenants__delete-btn"
                        onClick={() => openDeleteDialog(o)}
                      >
                        {t("adminTenants.deleteButton")}
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <div
        className="admin-tenants__panel"
        role="tabpanel"
        id="admin-tenants-panel-removals"
        aria-labelledby="admin-tenants-tab-removals"
        data-testid="admin-tenants-panel-removals"
        hidden={tab !== "removals"}
        tabIndex={0}
      >
        <div className="admin-tenants__card admin-tenants__list">
          <h2 className="admin-tenants__list-title">{t("adminTenants.listTitle")}</h2>

          {removalsQuery.isError && (
            <div className="admin-tenants__error-banner">{t("adminTenants.loadError")}</div>
          )}

          <div className="admin-tenants__table-wrap">
            <table className="admin-tenants__table">
              <thead>
                <tr>
                  <th>{t("adminTenants.colTenant")}</th>
                  <th>{t("adminTenants.colStatus")}</th>
                  <th>{t("adminTenants.colRequestedBy")}</th>
                  <th>{t("adminTenants.colPurgeAfter")}</th>
                  <th>{t("adminTenants.colProgress")}</th>
                  <th aria-label={t("adminTenants.colActions")} />
                </tr>
              </thead>
              <tbody>
                {removals.length === 0 && (
                  <tr>
                    <td colSpan={6} className="admin-tenants__empty">
                      {t("adminTenants.empty")}
                    </td>
                  </tr>
                )}
                {removals.map((r) => (
                  <tr key={r.tenantRemovalId}>
                    <td>{r.organizationName}</td>
                    <td>
                      <span className="admin-tenants__badge">
                        {t(`adminTenants.status.${r.status}`)}
                      </span>
                      {r.status === "FAILED" && r.lastError && (
                        <div className="admin-tenants__error">{r.lastError}</div>
                      )}
                    </td>
                    <td>{r.requestedByEmail ?? "—"}</td>
                    <td>{when(r.purgeAfter)}</td>
                    <td>
                      {r.status === "PURGING" || r.status === "PURGED"
                        ? `${r.totalRowsDeleted} / ${r.previewTotalRows}`
                        : "—"}
                    </td>
                    <td className="admin-tenants__actions">
                      {r.status === "SCHEDULED" && (
                        <button
                          type="button"
                          className="admin-tenants__action-btn"
                          onClick={() => restoreRemoval.mutate(r.tenantRemovalId)}
                          disabled={restoreRemoval.isLoading}
                        >
                          {t("adminTenants.restore")}
                        </button>
                      )}
                      {r.status === "FAILED" && (
                        <button
                          type="button"
                          className="admin-tenants__action-btn"
                          onClick={() => retryRemoval.mutate(r.tenantRemovalId)}
                          disabled={retryRemoval.isLoading}
                        >
                          {t("adminTenants.retry")}
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      {deletingTenant && (
        <div className="admin-tenants__overlay">
          <div
            className="admin-tenants__overlay-backdrop"
            onClick={closeDeleteDialog}
            aria-hidden="true"
          />
          <div
            className="admin-tenants__dialog"
            role="dialog"
            aria-modal="true"
            aria-labelledby="admin-tenants-dialog-title"
            ref={dialogRef}
          >
            <h2 id="admin-tenants-dialog-title" className="admin-tenants__dialog-title">
              {t("adminTenants.popupTitle", { name: deletingTenant.name })}
            </h2>

            <p className="admin-tenants__warning">
              {t("adminTenants.warning", { name: deletingTenant.name })}
            </p>

            {previewQuery.data && (
              <p className="admin-tenants__rows">
                {t("adminTenants.rowsToDestroy")} <strong>{previewQuery.data.totalRows}</strong>
              </p>
            )}

            <label className="admin-tenants__field" htmlFor="admin-tenants-confirm-name">
              <span className="admin-tenants__field-label">
                {t("adminTenants.confirmNameLabel")}
              </span>
              <input
                id="admin-tenants-confirm-name"
                type="text"
                className="admin-tenants__confirm-input"
                value={confirmName}
                onChange={(e) => setConfirmName(e.target.value)}
                placeholder={deletingTenant.name}
                autoComplete="off"
              />
            </label>

            <div className="admin-tenants__dialog-actions">
              <button
                type="button"
                className="admin-tenants__cancel-btn"
                onClick={closeDeleteDialog}
              >
                {t("Cancel")}
              </button>
              <button
                type="button"
                className="admin-tenants__danger"
                disabled={!nameMatches || createRemoval.isLoading}
                onClick={submit}
              >
                {t("adminTenants.confirmButton")}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminTenants;
